import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import Reveal from '../components/Reveal';
import { LineIcon } from '../components/icons';
import { usePressSpring } from '../hooks/usePressSpring';
import { byId } from '../data/apps';
import * as cadetPilot from '../data/legal/cadetPilot';
import * as socialFridgeMagnets from '../data/legal/socialFridgeMagnets';
import * as habitBranches from '../data/legal/habitBranches';
import * as general from '../data/legal/general';
import { useLanguage } from '../i18n/LanguageContext';
import { cx } from '../utils/cx';
import mixins from '../styles/mixins.module.css';
import styles from './LegalPage.module.css';
import NotFound from './NotFound';

const LEGAL_BY_APP = {
  'cadet-pilot': cadetPilot,
  'social-fridge-magnets': socialFridgeMagnets,
  'habit-branches': habitBranches,
};

export default function LegalPage({ appId: appIdProp, kind }) {
  const { t, lang } = useLanguage();
  const params = useParams();
  const appId = appIdProp ?? params.appId;
  const app = appId ? byId(appId) : null;
  const navigate = useNavigate();
  const backRef = usePressSpring(mixins.pressed);

  const doc = app ? LEGAL_BY_APP[app.id] : general;
  const title = kind === 'privacy' ? t('privacyPolicy') : t('termsOfUse');

  useEffect(() => {
    document.title = `${app ? app.name + ' · ' : ''}${title} — OnyxUp`;
  }, [app, title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [appId, kind]);

  if (appId && !app) return <NotFound />;
  if (!doc) return <NotFound />;

  const localizedDoc = doc[lang] || doc.en;
  const content = kind === 'privacy' ? localizedDoc.privacyContent : localizedDoc.termsContent;

  return (
    <section className={cx(mixins.wrap, styles.legal)}>
      <button
        ref={backRef}
        className={cx(mixins.back, mixins.glass, mixins.spring, styles.back)}
        onClick={() => navigate(app ? `/app/${app.id}` : '/')}
      >
        <LineIcon name="chev" strokeWidth={2.6} /> {app ? app.name : t('backToHome')}
      </button>

      <Reveal>
        <GlassCard as="div" className={cx(mixins.card, styles.card)}>
          <h1 className={cx(mixins.ink, styles.h1)}>{title}</h1>
          <p className={styles.meta}>{t('legalLastUpdated')} {doc.updated}</p>
          {app && (
            <div className={mixins.appChip}>
              <div className={mixins.appIcon}>
                {app.icon ? <img src={app.icon} alt={app.name} /> : null}
              </div>
              <b>{app.fullName}</b>
            </div>
          )}
          <div className={styles.body}>{content}</div>
        </GlassCard>
      </Reveal>
    </section>
  );
}
